import { useCallback, useEffect, useRef, useState } from 'react';

/** Lifecycle states of a copy interaction. */
export const COPY_STATUS = {
  Idle: 'idle',
  Copied: 'copied',
} as const;

export type CopyStatus = (typeof COPY_STATUS)[keyof typeof COPY_STATUS];

/**
 * How long `status` stays on `copied` before reverting — the lifetime
 * of the in-place visual confirmation only.
 */
export const VISUAL_RESET_DELAY_MS = 500;

/**
 * How long `isAnnouncingCopy` stays true. Deliberately longer than
 * {@link VISUAL_RESET_DELAY_MS}: a polite live region queues behind
 * whatever the screen reader is already speaking, so text that appears
 * and clears within the visual's window can be dropped unread.
 */
export const ANNOUNCEMENT_RESET_DELAY_MS = 2000;

export type UseCopyToClipboardResult = {
  /**
   * Writes `text` to the system clipboard. Resolves `true` on success
   * (flipping `status` to `copied` for
   * {@link VISUAL_RESET_DELAY_MS} and `isAnnouncingCopy` to true for
   * {@link ANNOUNCEMENT_RESET_DELAY_MS}), `false` when the write is
   * rejected or the Clipboard API is unavailable. Never throws and
   * never surfaces UI of its own — the caller decides what failure
   * looks like.
   */
  copy: (text: string) => Promise<boolean>;
  /**
   * `copied` while the *visual* confirmation is showing, else `idle`.
   * Drives the glyph swap and active fill only — gate a screen-reader
   * announcement on `isAnnouncingCopy` instead, which is held longer
   * on purpose.
   */
  status: CopyStatus;
  /** True while the copy should be announced to assistive tech. */
  isAnnouncingCopy: boolean;
};

/**
 * Clipboard writer with a self-resetting success flag, for buttons
 * that confirm a copy in place rather than through a toast.
 */
export function useCopyToClipboard(): UseCopyToClipboardResult {
  const [status, setStatus] = useState<CopyStatus>(COPY_STATUS.Idle);
  const [isAnnouncingCopy, setIsAnnouncingCopy] = useState(false);
  const visualTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const announcementTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearTimeout(visualTimerRef.current);
      clearTimeout(announcementTimerRef.current);
    };
  }, []);

  const copy = useCallback(async (text: string): Promise<boolean> => {
    try {
      // Reading through `navigator` at call time (rather than
      // destructuring on render) keeps the hook working in
      // environments that install the Clipboard API late, and lets a
      // missing API fall into the same failure path as a rejection.
      await navigator.clipboard.writeText(text);
    } catch {
      return false;
    }

    if (!isMountedRef.current) {
      return true;
    }

    clearTimeout(visualTimerRef.current);
    clearTimeout(announcementTimerRef.current);
    setStatus(COPY_STATUS.Copied);
    setIsAnnouncingCopy(true);
    visualTimerRef.current = setTimeout(() => {
      visualTimerRef.current = undefined;
      setStatus(COPY_STATUS.Idle);
    }, VISUAL_RESET_DELAY_MS);
    announcementTimerRef.current = setTimeout(() => {
      announcementTimerRef.current = undefined;
      setIsAnnouncingCopy(false);
    }, ANNOUNCEMENT_RESET_DELAY_MS);

    return true;
  }, []);

  return { copy, status, isAnnouncingCopy };
}
